export const ORDER_COUNT_POLICY_VERSION = 4;
export const MONTHLY_THRESHOLD_POLICY_VERSION = 5;
export const CALCULATION_POLICY_VERSION = 5;

function numberOrZero(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function nonNegativeNumber(value) {
  return Math.max(numberOrZero(value), 0);
}

function calculationCell(data, designerCode, market, monthKey) {
  return data?.[designerCode]?.[market]?.[monthKey] || {};
}

function buildMonthResult({ monthKey, cell, threshold, exchangeRate, legacyEligible = null }) {
  const sale = nonNegativeNumber(cell.sale);
  const coefficient = nonNegativeNumber(cell.coefficient);
    const eligibleSale = legacyEligible === null
    ? (sale > threshold ? sale : 0)
    : legacyEligible ? sale : 0;
  const bonus = eligibleSale * coefficient * exchangeRate;

  return {
    monthKey,
    sale,
    threshold,
    eligibleSale,
    coefficient,
    exchangeRate,
    exceededThreshold: eligibleSale > 0,
    eligible: bonus > 0,
    bonus,
  };
}

function calculateLegacyMarketDesigner(data, designerCode, market, months, settings) {
  const threshold = nonNegativeNumber(settings?.thresholds?.[market]);
  const exchangeRate = nonNegativeNumber(settings?.exchangeRate);
  const periodSale = months.reduce(
    (sum, monthKey) => sum + nonNegativeNumber(calculationCell(data, designerCode, market, monthKey).sale),
    0,
  );
  const legacyEligible = periodSale > threshold;
  const monthResults = months.map((monthKey) => buildMonthResult({
    monthKey,
    cell: calculationCell(data, designerCode, market, monthKey),
    threshold,
    exchangeRate,
    legacyEligible,
  }));

  return {
    periodSale,
    periodEligibleSale: legacyEligible ? periodSale : 0,
    eligibleMonthCount: legacyEligible ? monthResults.filter((result) => result.sale > 0).length : 0,
    // Preserve the historical period-level eligibility flag exactly for v1-v4 snapshots.
    eligible: legacyEligible,
    bonus: monthResults.reduce((sum, result) => sum + result.bonus, 0),
    monthResults,
  };
}

function calculateMonthlyMarketDesigner(data, designerCode, market, months, settings) {
  const threshold = nonNegativeNumber(settings?.thresholds?.[market]);
  const exchangeRate = nonNegativeNumber(settings?.exchangeRate);
  const monthResults = months.map((monthKey) => buildMonthResult({
    monthKey,
    cell: calculationCell(data, designerCode, market, monthKey),
    threshold,
    exchangeRate,
  }));
  const bonus = monthResults.reduce((sum, result) => sum + result.bonus, 0);

  return {
    periodSale: monthResults.reduce((sum, result) => sum + result.sale, 0),
    periodEligibleSale: monthResults.reduce((sum, result) => sum + result.eligibleSale, 0),
    eligibleMonthCount: monthResults.filter((result) => result.eligible).length,
    eligible: bonus > 0,
    bonus,
    monthResults,
  };
}

export function calculateMarketDesigner(
  data,
  designerCode,
  market,
  months,
  settings,
  calculationPolicyVersion = CALCULATION_POLICY_VERSION,
) {
  if (Number(calculationPolicyVersion || 0) < MONTHLY_THRESHOLD_POLICY_VERSION) {
    return calculateLegacyMarketDesigner(data, designerCode, market, months, settings);
  }

  return calculateMonthlyMarketDesigner(data, designerCode, market, months, settings);
}

export function calculateMarketMonthResult(
  data,
  designerCode,
  market,
  monthKey,
  months,
  settings,
  calculationPolicyVersion = CALCULATION_POLICY_VERSION,
) {
  const result = calculateMarketDesigner(
    data,
    designerCode,
    market,
    months,
    settings,
    calculationPolicyVersion,
  );

  return result.monthResults.find((monthResult) => monthResult.monthKey === monthKey) || {
    monthKey,
    sale: 0,
    threshold: nonNegativeNumber(settings?.thresholds?.[market]),
    eligibleSale: 0,
    coefficient: 0,
    exchangeRate: nonNegativeNumber(settings?.exchangeRate),
    exceededThreshold: false,
    eligible: false,
    bonus: 0,
  };
}

export function calculateMarketMonthBonus(
  data,
  designerCode,
  market,
  monthKey,
  months,
  settings,
  calculationPolicyVersion = CALCULATION_POLICY_VERSION,
) {
  return calculateMarketMonthResult(
    data,
    designerCode,
    market,
    monthKey,
    months,
    settings,
    calculationPolicyVersion,
  ).bonus;
}

export function calculateDesignerMonthBonus(
  data,
  designerCode,
  monthKey,
  months,
  settings,
  markets,
  calculationPolicyVersion = CALCULATION_POLICY_VERSION,
) {
  return markets.reduce(
    (sum, market) => sum + calculateMarketMonthBonus(
      data,
      designerCode,
      market,
      monthKey,
      months,
      settings,
      calculationPolicyVersion,
    ),
    0,
  );
}

export function applyDefaultCoefficientsToBonusData(data, defaultCoefficients) {
  return Object.fromEntries(
    Object.entries(data || {}).map(([designerCode, markets]) => [
      designerCode,
      Object.fromEntries(
        Object.entries(markets || {}).map(([market, monthCells]) => {
          const hasDefault = Object.prototype.hasOwnProperty.call(defaultCoefficients || {}, market);
          const coefficient = hasDefault
            ? nonNegativeNumber(defaultCoefficients[market])
            : null;

          return [
            market,
            Object.fromEntries(
              Object.entries(monthCells || {}).map(([monthKey, cell]) => [
                monthKey,
                hasDefault ? { ...cell, coefficient } : { ...cell },
              ]),
            ),
          ];
        }),
      ),
    ]),
  );
}

export function filterRowsWithBonus(rows) {
  return (rows || []).filter((row) => numberOrZero(row?.totalBonus) > 0);
}
